import { useEffect, useMemo, useState } from "react";
import {
  api,
  desregistrarObjectUrl,
  fetchBinarioAutenticado,
  registrarObjectUrl,
  type Fragmento,
  type FragmentosPagina,
  type PaginaArtefacto,
  type PaginasArtefacto,
} from "./api";

// Raster DPI used by the gateway when rendering scanned pages (ingest OCR_DPI).
export const RASTER_DPI = 200;
const ANCHO_RENDER = 780;

export function esBboxUtil(bbox: number[] | null): boolean {
  return Array.isArray(bbox) && bbox.length === 4 && bbox.some((v) => v !== 0);
}

/** Geometry (percent) of a pdf-line bbox [left, bottom, right, top] in PDF units. */
export function geometriaTexto(bbox: number[], pagina: PaginaArtefacto): {
  left: string; top: string; width: string; height: string;
} {
  const [left, bottom, right, top] = bbox;
  return {
    left: `${(left / pagina.width) * 100}%`,
    top: `${((pagina.height - top) / pagina.height) * 100}%`,
    width: `${((right - left) / pagina.width) * 100}%`,
    height: `${((top - bottom) / pagina.height) * 100}%`,
  };
}

/** Geometry (percent) of an ocr-word bbox [left, top, width, height] in raster px. */
export function geometriaOcr(bbox: number[], pagina: PaginaArtefacto): {
  left: string; top: string; width: string; height: string;
} {
  const rasterW = (pagina.width * RASTER_DPI) / 72;
  const rasterH = (pagina.height * RASTER_DPI) / 72;
  const [left, top, width, height] = bbox;
  return {
    left: `${(left / rasterW) * 100}%`,
    top: `${(top / rasterH) * 100}%`,
    height: `${(height / rasterH) * 100}%`,
    width: `${(width / rasterW) * 100}%`,
  };
}

/** True when a fragments response matches the artifact+page currently shown (H-L3). */
export function fragmentosVigentes(f: FragmentosPagina | null, artifactId: string, paginaIndex: number | null): boolean {
  return f !== null && f.artifact_id === artifactId && f.page_index === paginaIndex;
}

export function VisorArtefacto({ artefactoId, volver }: { artefactoId: string; volver: () => void }) {
  const [paginas, setPaginas] = useState<PaginasArtefacto | null>(null);
  const [paginaIndex, setPaginaIndex] = useState<number | null>(null);
  const [fragmentos, setFragmentos] = useState<FragmentosPagina | null>(null);
  const [error, setError] = useState("");
  const [cargandoFragmentos, setCargandoFragmentos] = useState(false);
  // H-L2: raster fetched with credentials and painted through a blob: URL
  // whose lifecycle this component controls (revoke on change/unmount).
  const [rasterUrl, setRasterUrl] = useState<string | null>(null);
  const [cargandoRaster, setCargandoRaster] = useState(false);

  useEffect(() => {
    let activo = true;
    setPaginas(null);
    setPaginaIndex(null);
    setFragmentos(null);
    setError("");
    api.paginasArtefacto(artefactoId)
      .then((p) => {
        if (!activo) return;
        if (!p.pages.length) throw new Error("el artefacto no tiene páginas con fragmentos");
        setPaginas(p);
        setPaginaIndex(p.pages[0].page_index);
      })
      .catch((e) => {
        if (activo) setError(String(e));
      });
    return () => { activo = false; };
  }, [artefactoId]);

  // H-L3: reset fragment state on every page change; paint only when the
  // response matches the current artifact+page selection.
  useEffect(() => {
    if (paginaIndex === null) return;
    let activo = true;
    setFragmentos(null);  // stale highlights from the previous page never paint
    setCargandoFragmentos(true);
    api.fragmentosPagina(artefactoId, paginaIndex)
      .then((f) => { if (activo && fragmentosVigentes(f, artefactoId, paginaIndex)) setFragmentos(f); })
      .catch((e) => { if (activo) setError(String(e)); })
      .finally(() => { if (activo) setCargandoFragmentos(false); });
    return () => { activo = false; };
  }, [artefactoId, paginaIndex]);

  const paginaActual = useMemo(
    () => paginas?.pages.find((p) => p.page_index === paginaIndex) ?? null,
    [paginas, paginaIndex],
  );

  // H-L2 + N2: authenticated binary fetch → blob: URL with controlled
  // lifecycle. N2: every created URL is registered centrally so
  // cerrarSesion() revokes it even while this viewer stays mounted.
  useEffect(() => {
    if (!paginaActual || paginaActual.mode !== "ocr") {
      setRasterUrl((previo) => {
        if (previo) {
          URL.revokeObjectURL(previo);
          desregistrarObjectUrl(previo);
        }
        return null;
      });
      return;
    }
    let activo = true;
    let urlCreada: string | null = null;
    setCargandoRaster(true);
    fetchBinarioAutenticado(api.rasterPaginaUrl(artefactoId, paginaActual.page_index))
      .then((blob) => {
        if (!activo) return;
        urlCreada = URL.createObjectURL(blob);
        registrarObjectUrl(urlCreada); // N2: revocable on logout
        setRasterUrl((previo) => {
          if (previo) {
            URL.revokeObjectURL(previo);
            desregistrarObjectUrl(previo);
          }
          return urlCreada;
        });
      })
      .catch((e) => { if (activo) setError(String(e)); })
      .finally(() => { if (activo) setCargandoRaster(false); });
    return () => {
      activo = false;
      if (urlCreada) {
        URL.revokeObjectURL(urlCreada);
        desregistrarObjectUrl(urlCreada);
      }
    };
  }, [artefactoId, paginaActual?.page_index, paginaActual?.mode]);

  if (error) {
    return (
      <section aria-label="visor de artefacto">
        <p role="alert">Operación bloqueada: no se puede mostrar el artefacto ({error}).</p>
        <p><button className="sec" onClick={volver}>← volver</button></p>
      </section>
    );
  }
  if (!paginas || !paginaActual) return <p role="status">cargando visor…</p>;

  const conBbox = (fragmentos?.fragments ?? []).filter((f) => esBboxUtil(f.bbox));
  const sinBbox = (fragmentos?.fragments ?? []).filter((f) => !esBboxUtil(f.bbox));

  return (
    <section aria-label="visor de artefacto">
      <h2>Artefacto: {paginas.external_name}</h2>
      <p>
        <span className="badge estado">{paginas.pages.length} página(s)</span>{" "}
        <small>sha256 {paginas.sha256.slice(0, 10)}… · {paginas.size_bytes} B</small>
      </p>
      <nav aria-label="selector de página" className="paginas">
        {paginas.pages.map((p) => (
          <button
            key={p.page_index}
            className={p.page_index === paginaIndex ? "" : "sec"}
            onClick={() => setPaginaIndex(p.page_index)}
          >
            pág {p.page_index + 1} · {p.mode === "texto" ? "texto" : "OCR"} ({p.fragmentos})
          </button>
        ))}
      </nav>
      {paginaActual.mode === "texto" ? (
        <div
          className="pagina-texto"
          aria-label={`página ${paginaActual.page_index + 1} texto posicionado`}
          style={{ width: ANCHO_RENDER, aspectRatio: `${paginaActual.width} / ${paginaActual.height}` }}
        >
          {conBbox.filter((f) => f.kind === "pdf-line").map((f) => {
            const g = geometriaTexto(f.bbox as number[], paginaActual);
            return (
              <span
                key={f.id}
                className="fragmento resaltado"
                style={g}
                title={`${f.tool}: ${f.text}`}
              >
                {f.text}
              </span>
            );
          })}
        </div>
      ) : (
        <div
          className="pagina-raster"
          aria-label={`página ${paginaActual.page_index + 1} escaneada`}
          style={{ width: ANCHO_RENDER }}
        >
          {rasterUrl ? (
            <img
              src={rasterUrl}
              alt={`raster 200 DPI página ${paginaActual.page_index + 1}`}
              style={{ width: "100%" }}
            />
          ) : (
            <p role="status">cargando raster autenticado…</p>
          )}
          {conBbox.filter((f) => f.kind === "ocr-word").map((f) => {
            const g = geometriaOcr(f.bbox as number[], paginaActual);
            return (
              <span key={f.id} className="fragmento resaltado ocr" style={g} title={`${f.tool} (conf ${f.confidence}): ${f.text}`} />
            );
          })}
        </div>
      )}
      <p role="status">
        {cargandoFragmentos
          ? "cargando fragmentos…"
          : `${conBbox.length} fragmento(s) resaltado(s)${sinBbox.length ? `, ${sinBbox.length} sin bbox (lista abajo)` : ""}`}
      </p>
      {sinBbox.length > 0 && (
        <details>
          <summary>Fragmentos sin bbox ({sinBbox.length})</summary>
          <ul>{sinBbox.map((f) => <li key={f.id}>{f.text}</li>)}</ul>
        </details>
      )}
      <p><button className="sec" onClick={volver}>← volver</button></p>
    </section>
  );
}
